import type { ReactNode } from 'react';
import type { User } from '@reflex/shared';
import { UserAvatar } from '@/components/shared/UserAvatar';

interface MemberRowProps {
  /**
   * Resolved user record. When undefined, the row falls back to displaying
   * the raw `userIdFallback` so the UI never disappears just because the
   * user lookup is still loading.
   */
  user?: User;
  /**
   * Stable user id used as the avatar fallback display, when `user` is missing.
   */
  userIdFallback: string;
  /**
   * Optional trailing slot — typically a remove button, role editor, or both.
   */
  rightSlot?: ReactNode;
  /**
   * Optional `data-testid` for the wrapping `<li>`. Callers should namespace
   * the id (for example `team-member-row-${userId}`) so multiple lists on the
   * same page can be disambiguated.
   */
  testId?: string;
}

/**
 * Renders a single member row: avatar + display name + handle. The display
 * resolves to `user.name ?? user.login ?? userIdFallback`. When the user
 * record is missing, the handle area falls back to the raw user id in
 * monospace so it is still copyable while looking distinct from a real handle.
 */
export function MemberRow({ user, userIdFallback, rightSlot, testId }: MemberRowProps) {
  const display = user?.name ?? user?.login ?? userIdFallback;
  return (
    <li
      className="flex items-center justify-between gap-2 p-3"
      {...(testId ? { 'data-testid': testId } : {})}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <UserAvatar
          userId={user?.id}
          userName={display}
          avatarUrl={user?.avatarUrl ?? undefined}
          size="sm"
        />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm">{display}</span>
          {user ? (
            <span className="truncate text-xs text-muted-foreground">@{user.login}</span>
          ) : (
            <span className="truncate font-mono text-xs text-muted-foreground">
              {userIdFallback}
            </span>
          )}
        </div>
      </div>
      {rightSlot ? <div className="flex shrink-0 items-center gap-2">{rightSlot}</div> : null}
    </li>
  );
}
